import React, { useRef, useState } from 'react';
import SortableRowsV2 from './SortableRowsV2';
import KeyHandler from '@/components/KeyHandler';
import getElementClassName from '@/lib/getElementClassName';

const RowsV2 = ({ children, focusElement, rowClassName = '.comp-row-v2', className, enableKeyHandler = true, keyMap = {}, onClick, onDelete, onSort, style, scrollToElement = false }) => {
  const [activeIndex, setActiveIndex] = useState(null);
  const ref = useRef();

  const childrenCount = React.Children.count(children);
  const maxIndex = childrenCount - 1;

  let content = children;

  if (onSort) {
    content = (
      <SortableRowsV2 onSort={onSort}>
        {React.Children.map(children, (child, index) => {
          const existingClassName = child.props.className || '';
          const activeClassName = index === activeIndex ? 'selected' : '';
          const combinedClassName = [existingClassName, activeClassName].filter(Boolean).join(' ');

          return React.cloneElement(child, {
            className: combinedClassName,
            id: child.props?.sortable?.id || child.key
          });
        })}
      </SortableRowsV2>
    );
  }

  if (!onSort) {
    content = React.Children.map(children, (child, index) => {
      if (!focusElement) {
        return child;
      }
      const existingClassName = child.props.className || '';
      const activeClassName = index === activeIndex ? 'selected' : '';
      const combinedClassName = [existingClassName, activeClassName].filter(Boolean).join(' ');
      return React.cloneElement(child, { className: combinedClassName });
    });
  }

  const scrollToSelected = (index) => {
    const scrollContainer = ref.current?.parentElement;
    if (scrollContainer && (getElementClassName(scrollContainer).includes('comp-scroll-container-v2') || scrollToElement)) {
      const item = scrollContainer.querySelectorAll(rowClassName)[index];
      if (item) {
        item.scrollIntoView({ block: 'nearest', behavior: 'smooth' });
      }
    }
  };

  const handleKeyDown = (e) => {
    const indexIsNull = activeIndex === null;
    let newIndex = activeIndex;
    switch (e.key) {
      case 'ArrowDown':
        if (indexIsNull || activeIndex === maxIndex) {
          newIndex = 0;
        } else {
          newIndex = Math.min(activeIndex + 1, maxIndex);
        }
        break;
      case 'Shift+ArrowDown':
        // Need to convert activeIndex to activeIndexes and handle multiple selected indexes
        if (indexIsNull || activeIndex === maxIndex) {
          newIndex = 0;
        } else {
          newIndex = Math.min(activeIndex + 1, maxIndex);
        }
        break;
      case 'ArrowUp':
        if (indexIsNull || activeIndex === 0) {
          newIndex = maxIndex;
        } else {
          newIndex = Math.max(activeIndex - 1, 0);
        }
        break;
      case 'Shift+ArrowUp':
        // Need to convert activeIndex to activeIndexes and handle multiple selected indexes
        if (indexIsNull) {
          newIndex = maxIndex;
        } else {
          newIndex = Math.max(activeIndex - 1, 0);
        }
        break;
      case 'Home':
        newIndex = 0;
        break;
      case 'End':
        newIndex = maxIndex;
        break;
      case 'PageDown':
        if (indexIsNull) {
          newIndex = 5;
        } else {
          newIndex = Math.min(activeIndex + 5, maxIndex);
        }
        break;
      case 'PageUp':
        if (indexIsNull) {
          newIndex = maxIndex - 5;
        } else {
          newIndex = Math.max(activeIndex - 5, 0);
        }
        break;
      case 'Delete':
      case 'Meta+Backspace':
        onDelete?.(e, activeIndex);
        break;
      case 'Enter':
        onClick?.(e, activeIndex);
        break;
      default:
        break;
    }
    setActiveIndex(newIndex);
    scrollToSelected(newIndex);
  };

  const handleDefaultKeydown = (e) => {
    // eslint-disable-next-line no-useless-escape
    if (/^[a-zA-Z0-9`~!@#$%^&*()\-_=+\[\]{}\\|;:'",.<>/?]$/.test(e.key)) {
      setActiveIndex(0);
    }
  };

  const defaultKeyMap = {
    ArrowDown: handleKeyDown,
    'Shift+ArrowDown': handleKeyDown,
    ArrowUp: handleKeyDown,
    'Shift+ArrowUp': handleKeyDown,
    Home: handleKeyDown,
    End: handleKeyDown,
    PageDown: handleKeyDown,
    PageUp: handleKeyDown,
    Delete: handleKeyDown,
    'Meta+Backspace': handleKeyDown,
    Enter: handleKeyDown,
    Default: handleDefaultKeydown
  };

  const activeKeyMap = enableKeyHandler ? { ...defaultKeyMap, ...keyMap } : null;
  const eventTarget = focusElement || ref;

  return (
    <div ref={ref} className={`comp-rows-v2 ${className || ''}`} tabIndex="-1" style={style}>
      {!!eventTarget && !!enableKeyHandler && <KeyHandler keyMap={activeKeyMap} stopPropagation={true} target={eventTarget} />}
      {content}
    </div>
  );
};

export default RowsV2;
